import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  tDicomAbstractName,
  tDicomAbstractDescription,
  tDicomTransferSyntaxName,
} from '../../../../helpers/i18n/dicomSyntaxI18n';
import { Modal, Button, Badge } from '../custom-components';
import { Device, Connection, ApplicationEntity } from '../types';
import { getAEStatusBadge } from '../utils/status-badges';
import {
  abstractSyntaxInfo,
  transferSyntaxInfo,
  DicomSyntaxConfig,
  AbstractSyntaxConfig,
} from '../resources/syntax-config';

interface AEViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  ae: ApplicationEntity | null;
  device: Device | null;
  connection: Connection | null;
}

export const AEViewModal: React.FC<AEViewModalProps> = ({
  isOpen,
  onClose,
  ae,
  device,
  connection,
}) => {
  const { t } = useTranslation('Organization');

  if (!ae) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('devices.modals.aeDetailsTitle')}
      description={t('devices.modals.aeViewDescription', { aeTitle: ae.aeTitle })}
      size="xl"
    >
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-medium text-gray-900 dark:text-white">
              {t('devices.modals.basicInformation')}
            </h3>
          </div>
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-gray-400">
                  {t('devices.modals.aeTitle')}
                </label>
                <div className="text-slate-900 dark:text-white font-mono">
                  {ae.aeTitle}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-gray-400">
                  {t('Status')}
                </label>
                <div>{getAEStatusBadge(ae.status)}</div>
              </div>
              {/* <div>
                <label className="text-sm font-medium text-slate-700">
                  DICOM Type
                </label>
                <div>
                  <Badge
                    variant={
                      ae.dicomType === 'standard' ? 'default' : 'secondary'
                    }
                  >
                    {ae.dicomType}
                  </Badge>
                </div>
              </div> */}
            </div>
            {ae.description && (
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-gray-400">
                  {t('devices.modals.description')}
                </label>
                <div className="text-slate-900 dark:text-white">
                  {ae.description}
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-gray-400">
                {t('devices.modals.aeView.connection')}
              </label>
              <div className="text-slate-900 dark:text-white font-mono">
                {connection?.hostname}:{connection?.port}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-medium text-gray-900 dark:text-white">
              {t('devices.modals.aeView.transferSyntaxCapabilities')}
            </h3>
            <p className="text-sm text-slate-600 dark:text-gray-400">
              {t('devices.modals.aeView.transferSyntaxHint')}
            </p>
          </div>
          <div className="p-4 space-y-4">
            {Object?.entries(abstractSyntaxInfo)?.map(([key, info]) => {
              const abstractKey = key as keyof DicomSyntaxConfig;
              const config = ae.syntaxConfig[abstractKey];
              const acceptedCount = Object?.values(config)?.filter(
                (value) => value === 'accepted'
              )?.length;
              const totalCount = Object?.keys(config)?.length;

              return (
                <div
                  key={key}
                  className="bg-slate-50 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-lg"
                >
                  <div className="px-4 py-3 border-b border-slate-200 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-slate-900 dark:text-white">
                          {tDicomAbstractName(key)}
                        </h4>
                        <p className="text-xs text-slate-600 dark:text-gray-400">
                          {tDicomAbstractDescription(key)}
                        </p>
                        <div className="text-xs text-slate-500 dark:text-gray-500 font-mono mt-1">
                          {info.sopClassUid}
                        </div>
                      </div>
                      <Badge
                        className={
                          acceptedCount > 0
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }
                      >
                        {t('devices.modals.configureAe.acceptedCount', { accepted: acceptedCount, total: totalCount })}
                      </Badge>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="space-y-3">
                      <div>
                        <h5 className="text-xs font-medium text-slate-700 dark:text-gray-400 mb-2">
                          {t('devices.common.uncompressed')}
                        </h5>
                        <div className="grid grid-cols-1 gap-1">
                          {Object.entries(transferSyntaxInfo)
                            .filter(([_, info]) => info.type === 'Uncompressed')
                            .map(([transferKey, transferInfo]) => {
                              if (!(transferKey in config)) return null;
                              const value =
                                config[
                                  transferKey as keyof AbstractSyntaxConfig
                                ];
                              return (
                                <div
                                  key={transferKey}
                                  className="flex items-center justify-between p-2 rounded border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                                >
                                  <div className="flex-1">
                                    <div className="text-xs font-medium text-slate-900 dark:text-white">
                                      {tDicomTransferSyntaxName(transferKey)}
                                    </div>
                                    <div className="text-xs text-slate-500 dark:text-gray-400 font-mono">
                                      {transferInfo.uid}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span
                                      className={`text-xs font-medium ${
                                        value === 'accepted'
                                          ? 'text-green-600 dark:text-green-400'
                                          : 'text-red-600 dark:text-red-400'
                                      }`}
                                    >
                                      {value === 'accepted'
                                        ? t('devices.common.accepted')
                                        : t('devices.common.rejected')}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>

                      <div>
                        <h5 className="text-xs font-medium text-slate-700 dark:text-gray-400 mb-2">
                          {t('devices.common.compressed')}
                        </h5>
                        <div className="grid grid-cols-1 gap-1">
                          {Object.entries(transferSyntaxInfo)
                            .filter(([_, info]) => info.type === 'Compressed')
                            .map(([transferKey, transferInfo]) => {
                              if (!(transferKey in config)) return null;
                              const value =
                                config[
                                  transferKey as keyof AbstractSyntaxConfig
                                ];
                              return (
                                <div
                                  key={transferKey}
                                  className="flex items-center justify-between p-2 rounded border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                                >
                                  <div className="flex-1">
                                    <div className="text-xs font-medium text-slate-900 dark:text-white">
                                      {tDicomTransferSyntaxName(transferKey)}
                                    </div>
                                    <div className="text-xs text-slate-500 dark:text-gray-400 font-mono">
                                      {transferInfo.uid}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span
                                      className={`text-xs font-medium ${
                                        value === 'accepted'
                                          ? 'text-green-600 dark:text-green-400'
                                          : 'text-red-600 dark:text-red-400'
                                      }`}
                                    >
                                      {value === 'accepted'
                                        ? t('devices.common.accepted')
                                        : t('devices.common.rejected')}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex justify-end pt-4">
        <Button variant="outline" onClick={onClose}>
          {t('devices.common.close')}
        </Button>
      </div>
    </Modal>
  );
};
